import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import FilterNewIcon from '../assets/svgIcons/FilterNewIcon';
import GlobalButton from './GlobalButton';
import {
  greyColor,
  primaryColor, primaryFont, primaryHeadingColor, scrollbar, secondaryFont, wMBold, wMedium,
} from '../../../Utils/utilsStyle';
import { AdvancedSearchMetadataIndustry } from '../../../Types/AdvanceSearchResultsTypes';
import { Checkbox } from 'antd';
import AlertsSearch from '../../alerts/components/AlertsSearch';
import { SubFilterSelection } from '../../../Types/GlobalTypes';

const StyleWrapper = styled.div`
  width: 340px;
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 16px 24px;
    flex-wrap: wrap;
    row-gap: 8px;
    .validation-message {
      width: 100%;
      color: #ff1f2d;
      font-family: ${primaryFont};
      font-size: 16px;
      font-weight: ${wMedium};
      line-height: 20px;
      text-align: center;
    }
    .title {
      display: flex;
      align-items: center;
      column-gap: 6px;
      .icon {
        svg {
          width: 22px;
          height: 20px;
          path {
            fill: #000 !important;
          }
        }
      }
      .text {
        color: ${primaryHeadingColor};
        font-family: ${secondaryFont};
        font-size: 20px;
        font-weight: ${wMBold};
        line-height: normal;
        letter-spacing: -0.609px;
      }
    }
  }
  .content-box {
    background: #F8F8F8;
    padding: 22px 16px;
    display: flex;
    flex-direction: column;
    row-gap: 12px;
    .selected-div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      .text {
        color: #67696A;
        font-family: ${primaryFont};
        font-size: 18px;
        font-weight: ${wMedium};
        line-height: 16px;
        letter-spacing: -0.375px;
      }
      .unselect-btn {
        color: ${primaryColor};
        font-family: ${primaryFont};
        font-size: 14px;
        font-weight: ${wMedium};
        line-height: 16px;
        letter-spacing: -0.4px;
        cursor: pointer;
      }
    }
    .search-box {
      .search-wrapper {
        margin-bottom: 0px;
        .ant-input-affix-wrapper {
          height: 32px;
          .ant-input {
            max-width: 220px;
          }
        }
        .cross-icon {
          top: 6px;
          right: 7px;
        }
      }
    }
    .data {
      width: 100%;
      display: flex;
      flex-direction: column;
      row-gap: 12px;
      height: 300px;
      border-radius: 0px !important;
      ${scrollbar}
      ::-webkit-scrollbar-track {
        background: transparent
      }
      ::-webkit-scrollbar-thumb {
        border-radius: 20px;
      }
      .filter-item {
        display: flex;
        align-items: center;
        column-gap: 6px;
        cursor: pointer;
        .ant-checkbox-wrapper {
          width: auto;
          margin-bottom: 0px;
        }
        .item-name {
          color: ${greyColor} !important;
          font-family: ${primaryFont};
          font-size: 16px;
          font-weight: ${wMedium};
          line-height: 20px;
          max-width: 230px;
        }
        
      }
      .no-results {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${greyColor} !important;
        }
    }
  }
`;

export type SubFilterOption = string | AdvancedSearchMetadataIndustry;

type Props = {
  category: string;
  title: string;
  data: SubFilterOption[];
  selection: SubFilterSelection;
  applyDisabled?: boolean;
  onSelectionChange?: (selection: SubFilterSelection) => void;
  onApply?: (category: string, selection: SubFilterSelection) => void;
};
const defaultProps = {
  category: '',
  title: '',
  data: [] as SubFilterOption[],
  applyDisabled: false,
  onSelectionChange() { },
  onApply() { },
};
const SubFilterComponent = function SubFilterComponent(props: Props) {
  const {
    category, title, data, selection, applyDisabled, onSelectionChange, onApply,
  } = props;

  const [searchText, setSearchText] = useState('');
  const showSearch = category !== 'companySize';

  const getOptionName = (option: SubFilterOption) => {
    if (typeof option === 'string') {
      return option;
    }
    return option.name;
  };

  const allOptions = useMemo(() => (
    (data ?? []).map(getOptionName).filter(Boolean)
  ), [data]);

  const filteredData = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    if (!showSearch || !query) {
      return data ?? [];
    }

    return (data ?? []).filter((option) =>
      getOptionName(option).toLowerCase().includes(query),
    );
  }, [data, searchText, showSearch]);

  const visibleOptionLabels = useMemo(
    () => filteredData.map(getOptionName).filter(Boolean),
    [filteredData],
  );

  const isOptionSelected = (optionName: string) => (
    selection.all
      ? !selection.excluded.includes(optionName)
      : selection.selected.includes(optionName)
  );

  const areAllVisibleSelected = visibleOptionLabels.length > 0
    && visibleOptionLabels.every((option) => isOptionSelected(option));

  const hasActiveSearch = searchText.trim().length > 0;
  const hasNoSelection = !selection.all && selection.selected.length === 0;

  const handleSelection = (option: SubFilterOption) => {
    const optionName = getOptionName(option);

    if (selection.all) {
      const isExcluded = selection.excluded.includes(optionName);
      onSelectionChange?.({
        ...selection,
        excluded: isExcluded
          ? selection.excluded.filter((item) => item !== optionName)
          : [...selection.excluded, optionName],
      });
      return;
    }

    const isSelected = selection.selected.includes(optionName);
    onSelectionChange?.({
      ...selection,
      selected: isSelected
        ? selection.selected.filter((item) => item !== optionName)
        : [...selection.selected, optionName],
    });
  };

  const handleToggleAll = () => {
    if (!hasActiveSearch) {
      if (areAllVisibleSelected) {
        onSelectionChange?.({
          all: false,
          selected: [],
          excluded: [],
        });
        return;
      }

      onSelectionChange?.({
        all: true,
        selected: [],
        excluded: [],
      });
      return;
    }

    if (areAllVisibleSelected) {
      if (selection.all) {
        onSelectionChange?.({
          ...selection,
          excluded: [
            ...new Set([
              ...selection.excluded,
              ...visibleOptionLabels,
            ]),
          ],
        });
        return;
      }

      onSelectionChange?.({
        ...selection,
        selected: selection.selected.filter(
          (option) => !visibleOptionLabels.includes(option),
        ),
      });
      return;
    }

    if (selection.all) {
      onSelectionChange?.({
        ...selection,
        excluded: selection.excluded.filter(
          (option) => !visibleOptionLabels.includes(option),
        ),
      });
      return;
    }

    onSelectionChange?.({
      ...selection,
      selected: [
        ...new Set([
          ...selection.selected,
          ...visibleOptionLabels,
        ]),
      ],
    });
  };

  const handleApply = () => {
    onApply?.(category, selection);
  };

  return (
    <StyleWrapper className="sub-filter-component">
      <div className="header">
        <div className="title">
          <div className="icon">
            <FilterNewIcon />
          </div>
          <div className="text">
            Filter
          </div>
        </div>
        <GlobalButton
          title="Apply"
          outlineColor={primaryColor}
          bgColor={primaryColor}
          textColor="#fff"
          borderRadius="8px"
          innerSpace="7px 15px"
          buttonClassName="apply-button"
          onButtonClick={handleApply}
          disabled={applyDisabled || hasNoSelection}
        />
        {(hasNoSelection && filteredData.length > 0) && (
          <div className="validation-message">
            Atleast one filter should be selected
          </div>
        )}
      </div>
      <div className="content-box">
        <div className="selected-div">
          <div className="text">
            {`Select ${title}:`}
          </div>
          {
            allOptions.length > 0 && (
              <div
                className="unselect-btn"
                role="button"
                tabIndex={0}
                onClick={handleToggleAll}
              >
                {areAllVisibleSelected ? 'unselect all' : 'select all'}
              </div>
            )
          }
        </div>
        {
          showSearch && (
            <div className='search-box'>
              <AlertsSearch
                placeholderText={`Search ${title}`}
                margin='0 0 10px'
                query={searchText}
                onSearching={() => { }}
                onInputChange={(value?: string) => setSearchText(value ?? '')}
                enableQueryTrim={false}
              />
            </div>
          )
        }

        <div className='data'>
          {
            filteredData.length > 0 && filteredData?.map((item) => {
              const name = getOptionName(item);
              const isSelected = isOptionSelected(name);
              return (
                <div
                  className='filter-item'
                  key={name}
                  onClick={() => handleSelection(item)}
                >
                  <Checkbox
                    checked={isSelected}
                  />
                  <span className='item-name text-truncate'>
                    {name}
                  </span>
                </div>
              )
            })
          }
          {
            filteredData.length === 0 && (
              <div className='no-results'>
                No Results Found.
              </div>
            )
          }
        </div>

      </div>

    </StyleWrapper>
  );
};

SubFilterComponent.defaultProps = defaultProps;
export default SubFilterComponent;
